import { useBodyPalette } from "../../scene/BodyFinish";
import { Chassis } from "../../scene/Chassis";
import { TAIL_LIGHT } from "../../scene/materials";
import {
  TRAILER_12,
  TRAILER_8,
  trailerFrontX,
  trailerTailX
} from "./dimensions";
function TrailerBody({ dimensions: d }) {
  const palette = useBodyPalette("trailer");
  const boxHeight = d.boxTopY - d.deckY;
  const boxCentreY = d.deckY + boxHeight / 2;
  const halfWidth = d.width / 2;
  const wall = 0.06;
  const frontX = trailerFrontX(d);
  const tailX = trailerTailX(d);
  const ribPositions = () => {
    const span = d.length - 2.2;
    const step = span / (d.ribCount - 1);
    return Array.from({ length: d.ribCount }, (_, i) => -span / 2 + i * step);
  };
  const rearmostAxle = Math.min(...d.axleX);
  const bogieCentre = d.axleX.reduce((sum, x) => sum + x, 0) / d.axleX.length;
  return <group>
      {
    /*
      Deck slab is SHELL despite a chassis-coloured material: it is the mesh a ray hits on its
      way to an inner dual. Material identity is the wrong classifier.
    */
  }
      <mesh position={[0, d.deckY - 0.08, 0]}>
        <boxGeometry args={[d.length, 0.16, d.width]} />
        <meshPhysicalMaterial {...palette.chassis} />
      </mesh>
      <Chassis>
        <mesh position={[d.centreBeam.x, d.chassisY - 0.14, 0]}>
          <boxGeometry args={[d.centreBeam.length, 0.28, 0.86]} />
          <meshPhysicalMaterial {...palette.chassis} />
        </mesh>
      </Chassis>

      {[-1, 1].map((side) => <mesh key={`wall-${side}`} position={[0, boxCentreY, side * (halfWidth - wall / 2)]}>
          <boxGeometry args={[d.length, boxHeight, wall]} />
          <meshPhysicalMaterial {...palette.body} />
        </mesh>)}
      <mesh position={[frontX - 0.04, boxCentreY, 0]}>
        <boxGeometry args={[0.07, boxHeight, d.width]} />
        <meshPhysicalMaterial {...palette.body} />
      </mesh>
      <mesh position={[0, d.boxTopY - 0.04, 0]}>
        <boxGeometry args={[d.length, 0.07, d.width]} />
        <meshPhysicalMaterial {...palette.body} />
      </mesh>

      {ribPositions().map(
    (x) => [-1, 1].map((side) => <mesh key={`post-${x}-${side}`} position={[x, boxCentreY, side * (halfWidth + 0.02)]}>
            <boxGeometry args={[0.09, boxHeight - 0.14, 0.07]} />
            <meshPhysicalMaterial {...palette.edge} />
          </mesh>)
  )}

      {[-1, 1].map((side) => <mesh
    key={`door-${side}`}
    position={[tailX + 0.05, boxCentreY, side * (halfWidth / 2 - 0.02)]}
  >
          <boxGeometry args={[0.07, boxHeight - 0.1, halfWidth - 0.1]} />
          <meshPhysicalMaterial {...palette.secondary} />
        </mesh>)}
      {[-1, 1].map((side) => <mesh
    key={`door-post-${side}`}
    position={[tailX + 0.03, boxCentreY, side * (halfWidth - 0.05)]}
  >
          <boxGeometry args={[0.11, boxHeight, 0.11]} />
          <meshPhysicalMaterial {...palette.body} />
        </mesh>)}

      <Chassis>
        <mesh position={[d.kingpinX + 0.4, d.chassisY - 0.05, 0]}>
          <boxGeometry args={[1.9, 0.1, 1.45]} />
          <meshPhysicalMaterial {...palette.chassis} />
        </mesh>
        <mesh position={[d.kingpinX, d.chassisY - 0.17, 0]}>
          <cylinderGeometry args={[0.09, 0.09, 0.16, 20]} />
          <meshPhysicalMaterial {...palette.edge} />
        </mesh>
      </Chassis>

      <Chassis>
        {[-1, 1].map((side) => <group key={`leg-${side}`}>
            <mesh position={[d.landingLegX, 0.53, side * 0.82]}>
              <boxGeometry args={[0.15, 1.06, 0.15]} />
              <meshPhysicalMaterial {...palette.chassis} />
            </mesh>
            <mesh position={[d.landingLegX, 0.04, side * 0.82]}>
              <boxGeometry args={[0.4, 0.08, 0.24]} />
              <meshPhysicalMaterial {...palette.edge} />
            </mesh>
          </group>)}
      </Chassis>

      <Chassis>
        <mesh position={[bogieCentre, 0.94, 0]}>
          <boxGeometry args={[d.bogieLength, 0.2, 1.72]} />
          <meshPhysicalMaterial {...palette.chassis} />
        </mesh>
      </Chassis>

      <Chassis>
        {[-1, 1].map((side) => <mesh
    key={`mudflap-${side}`}
    position={[rearmostAxle - d.wheelRadius - 0.16, 0.5, side * d.dualHalfTrack]}
  >
            <boxGeometry args={[0.03, 0.36, 0.66]} />
            <meshPhysicalMaterial {...palette.chassis} />
          </mesh>)}
        <mesh position={[tailX + 0.1, 0.58, 0]}>
          <boxGeometry args={[0.1, 0.14, 2.2]} />
          <meshPhysicalMaterial {...palette.chassis} />
        </mesh>
        {[-1, 1].map((side) => <mesh key={`underrun-hanger-${side}`} position={[tailX + 0.12, 0.86, side * 0.75]}>
            <boxGeometry args={[0.07, 0.5, 0.09]} />
            <meshPhysicalMaterial {...palette.chassis} />
          </mesh>)}
      </Chassis>
      {[-1, 1].map((side) => <mesh
    key={`tail-light-${side}`}
    position={[tailX - 0.02, 1.02, side * (halfWidth - 0.34)]}
  >
          <boxGeometry args={[0.09, 0.26, 0.32]} />
          <meshPhysicalMaterial {...TAIL_LIGHT} />
        </mesh>)}
      {[-1, 1].flatMap(
    (side) => [-0.35, 0, 0.35].map((along) => <mesh
      key={`marker-${side}-${along}`}
      position={[along * (d.length / 2 - 1.2), d.deckY + 0.18, side * (halfWidth + 0.05)]}
    >
            <boxGeometry args={[0.1, 0.08, 0.06]} />
            <meshPhysicalMaterial {...palette.accent} />
          </mesh>)
  )}
    </group>;
}
const Trailer12Body = () => <TrailerBody dimensions={TRAILER_12} />;
const Trailer8Body = () => <TrailerBody dimensions={TRAILER_8} />;
export {
  Trailer12Body,
  Trailer8Body,
  Trailer12Body as TrailerBody
};
